/* REACT */
import React, { useEffect, useState, lazy, Suspense } from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import queryString from 'query-string'
import cookie from 'react-cookies'
import posthog from 'posthog-js'

/* @ts-ignore */
import { Helmet } from 'react-helmet'

/* COMPONENTS */
import Chat from './modes/Chat/Chat'
import Search from './modes/Search/Search'
import ErrorModal from './components/modals/error/ErrorModal'

/* STORE */
import {
  setSocketListeners,
  setIsInternalCase,
  setGnerateAiQueryResponse,
  setIsInternalCaseInQueryOrOptions,
  setIsTestCase,
  setBrand,
} from './store/maya/actions'

/* STYLES */

/* UTILS/CONSTANTS */
import { getClientSpecificScripts } from './utils/constants'
import { Analytics, logToConsole } from './utils'

/* LAZY LOADED CLIENT COMPONENTS */
const FrenchMorningComponent = lazy(
  () => import('./clientComponents/frenchmorning')
)
const FrenchMorningCanadaComponent = lazy(
  () => import('./clientComponents/frenchmorningcanada')
)
const FrenchMorningLondonComponent = lazy(
  () => import('./clientComponents/frenchmorninglondon')
)

/* ASSETS */

const ThemeProvider = React.memo(
  ({
    children,
    organizationSlug,
  }: {
    children: React.ReactNode
    organizationSlug: string
  }) => {
    const globalDetails = {
      organizationSlug: organizationSlug,
    }

    return (
      <StyledThemeProvider theme={globalDetails}>
        {children}
      </StyledThemeProvider>
    )
  },
  (prevProps, nextProps) => {
    // Memoization based on organizationSlug
    return prevProps.organizationSlug === nextProps.organizationSlug
  }
)

function Widget({
  organizationDetailsError,
  organizationDetails,
  organizationKey,
  organizationSlug,
  language,
  widgetMode = 'full-page',
  chatMode = 'b2c',
  usageMode,
  brand = null,
  setIsInternalCase,
  setGnerateAiQueryResponse,
  setIsInternalCaseInQueryOrOptions,
  setIsTestCase,
  setBrand,
}: {
  organizationDetailsError: string
  organizationDetails: any
  organizationKey: string
  organizationSlug: string
  language: string
  widgetMode: string
  chatMode: string
  usageMode?: string
  brand?: string | null
  setIsInternalCase: (value: boolean) => void
  setGnerateAiQueryResponse: (value: boolean) => void
  setIsInternalCaseInQueryOrOptions: (value: boolean) => void
  setIsTestCase: (value: boolean) => void
  setBrand: (value: string) => void
}) {
  const [organizationDetailsChecked, setOrganizationDetailsChecked] =
    useState(false)
  const [shouldLoadClientComponent, setShouldLoadClientComponent] = useState('')

  useEffect(() => {
    if (!shouldLoadClientComponent) {
      if (organizationSlug === 'frenchmorning') {
        setShouldLoadClientComponent('frenchmorning')
      } else if (organizationSlug === 'frenchmorningcanada') {
        setShouldLoadClientComponent('frenchmorningcanada')
      } else if (organizationSlug === 'frenchmorninglondon') {
        setShouldLoadClientComponent('frenchmorninglondon')
      }
    }
  }, [organizationSlug])

  useEffect(() => {
    if (!organizationDetailsChecked && organizationDetails) {
      // INITIALIZE ANALYTICS
      if (Analytics.getInstance()) {
        /* @ts-ignore */
        Analytics.getInstance().initialize({
          mayaIdGA4: organizationDetails?.mayaIdGA4 || null,
          clientIdGA4: organizationDetails?.clientIdGA4 || null,
        })
      }

      setOrganizationDetailsChecked(true) // SET TO TRUE

      if (
        process.env.REACT_APP_POSTHOG_API_KEY &&
        process.env.REACT_APP_POSTHOG_API_HOST &&
        process.env.REACT_APP_API_ENV === 'production' &&
        organizationDetails.enablePosthogAnalytics // IF ORGANIZATION HAS ENABLE POSTHOG ANALYTICS SET IN DB
      ) {
        // INITIALIZE POSTHOG
        posthog.init(process.env.REACT_APP_POSTHOG_API_KEY, {
          api_host: process.env.REACT_APP_POSTHOG_API_HOST,
          person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
          autocapture: false, // Disable automatic event capturing
          capture_pageview: true, // Disable automatic pageview capturing
          capture_pageleave: true, // Disable automatic pageleave capturing
        })
      }
    }
  }, [organizationDetails])

  useEffect(() => {
    // HANDLE IS INTERNAL CASE
    const { query } = queryString.parseUrl(window.location.search)

    // SET IS TEST CHAT TO TRUE
    if (cookie.load(`maya_is_test_case`)) {
      setIsTestCase(true)
      cookie.save(
        `maya_is_test_case`,
        'true',
        { path: '/', maxAge: 604800, domain: window.location.hostname } // SAVE COOKIE FOR 7 DAYS
      )
    } else if (query?.test) {
      cookie.save(
        `maya_is_test_case`,
        'true',
        { path: '/', maxAge: 604800, domain: window.location.hostname } // SAVE COOKIE FOR 7 DAYS
      )
      setIsTestCase(true)
    }

    // SET IS INTERNAL CASE TO TRUE IF INTERNAL IS IN QUERY PARAMS
    // ALSO DO NOT AUTO GENERATE AI QUERY RESPONSES
    if (query?.internal) {
      setIsInternalCaseInQueryOrOptions(true)
      setIsInternalCase(true)
      setGnerateAiQueryResponse(false)
      cookie.save(
        `maya_is_internal_case`,
        'true',
        { path: '/', maxAge: 604800, domain: window.location.hostname } // SAVE COOKIE FOR 7 DAYS
      )
    }

    // SET IS INTERNAL CASE TO TRUE IF INTERNAL IS IN COOKIES
    // GENERATE AI QUERY RESPONSES THOUGH
    if (cookie.load(`maya_is_internal_case`)) {
      setIsInternalCase(true)
      cookie.save(
        `maya_is_internal_case`,
        'true',
        { path: '/', maxAge: 604800, domain: window.location.hostname } // SAVE COOKIE FOR 7 DAYS
      )
    }

    // SET IS INTERNAL CASE TO TRUE IF INTERNAL IS IN WIDGET OPTIONS
    // ALSO DO NOT AUTO GENERATE AI QUERY RESPONSES
    if (usageMode === 'internal') {
      // WHEN INTERNAL CASE USING WIDGET OPTIONS DON'T SET THE COOKIE FOR 7 DAYS, SO THAT OTHER URLS (OTHER THAN THE ONES EMPLOYEES USE) REMAIN UNEFFECTED
      setIsInternalCaseInQueryOrOptions(true)
      setIsInternalCase(true)
      setGnerateAiQueryResponse(false)
    }

    if (brand) {
      // SET THE BRAND TO BE SAVED IN CHAT
      setBrand(brand)
    }
  }, [])

  return (
    <>
      {/* ADD GOOGLE TAG MANAGER SCRIPT FOR GA4 */}
      {organizationDetails?.mayaIdGA4 || organizationDetails?.clientIdGA4 ? (
        <Helmet>
          {organizationDetails?.mayaIdGA4 ? (
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${organizationDetails?.mayaIdGA4}`}
            ></script>
          ) : null}
          {organizationDetails?.clientIdGA4 ? (
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${organizationDetails?.clientIdGA4}`}
            ></script>
          ) : null}
        </Helmet>
      ) : null}

      {shouldLoadClientComponent === 'frenchmorning' ? (
        <Suspense fallback={<div></div>}>
          <FrenchMorningComponent />
        </Suspense>
      ) : shouldLoadClientComponent === 'frenchmorningcanada' ? (
        <Suspense fallback={<div></div>}>
          <FrenchMorningCanadaComponent />
        </Suspense>
      ) : shouldLoadClientComponent === 'frenchmorninglondon' ? (
        <Suspense fallback={<div></div>}>
          <FrenchMorningLondonComponent />
        </Suspense>
      ) : null}

      <div
        className={
          widgetMode === 'search'
            ? 'maya-b2b-parent-container maya-b2b-wrapper maya-b2b-search-wrapper'
            : 'maya-b2b-parent-container maya-b2b-wrapper'
        }
        style={{ width: '100%', height: '100%' }}
      >
        {getClientSpecificScripts(organizationSlug)}
        <div
          className={
            widgetMode === 'search'
              ? 'maya-b2b-widget-wrapper maya-b2b-search-widget-wrapper'
              : 'maya-b2b-widget-wrapper'
          }
          style={{
            width: '100%',
            height: !organizationDetailsError ? '100%' : '100vh',
          }}
        >
          {organizationDetailsError ? (
            <ErrorModal isOpen={true} message={organizationDetailsError} />
          ) : widgetMode === 'search' ? (
            <ThemeProvider organizationSlug={organizationSlug}>
              <Search
                organizationKey={organizationKey}
                organizationSlug={organizationSlug}
                chatMode={chatMode}
                widgetMode={widgetMode}
                language={language}
              />
            </ThemeProvider>
          ) : widgetMode !== 'search' ? (
            <ThemeProvider organizationSlug={organizationSlug}>
              <Chat
                organizationKey={organizationKey}
                organizationSlug={organizationSlug}
                chatMode={chatMode}
                widgetMode={widgetMode}
                language={language}
              />
            </ThemeProvider>
          ) : null}
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  organizationDetails: state.maya.organizationDetails,
  organizationDetailsError: state.maya.organizationDetailsError,
})

const mapDispatchToProps = (dispatch: any) => ({
  setIsInternalCase: (value: boolean) => dispatch(setIsInternalCase(value)),
  setIsInternalCaseInQueryOrOptions: (value: boolean) =>
    dispatch(setIsInternalCaseInQueryOrOptions(value)),
  setGnerateAiQueryResponse: (value: boolean) =>
    dispatch(setGnerateAiQueryResponse(value)),
  setIsTestCase: (value: boolean) => dispatch(setIsTestCase(value)),
  setBrand: (value: string) => dispatch(setBrand(value)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Widget)
